import React, { useCallback, useEffect, useRef } from 'react'

import Box from '@mui/material/Box'
import { SxProps, Theme } from '@mui/material/styles'

import { useLazyAudioNode, useOwnedAudioContext } from '@/hooks/useOwnedAudioContext'

export type AudioVisualizerProps = {
  audioSrc: HTMLAudioElement | MediaStream
  sx?: SxProps<Theme>
}

export function AudioVisualizer({ audioSrc, sx = [] }: AudioVisualizerProps) {
  const audioContext = useOwnedAudioContext()
  const analyser = useLazyAudioNode(audioContext, (context) => context.createAnalyser())
  const gainNode = useLazyAudioNode(audioContext, (context) => context.createGain())
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const rafId = useRef<number>()

  // Placeholder for the audio element reference, initialized conditionally below
  const audioRef = useRef<HTMLAudioElement | null>(null)

  const draw = useCallback(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    const bufferLength = analyser.current.frequencyBinCount
    const dataArray = new Uint8Array(bufferLength)
    analyser.current.getByteTimeDomainData(dataArray)

    const height = canvas.height
    const width = canvas.width

    const context = canvas.getContext('2d')
    if (!context) return

    let x = 0
    const sliceWidth = width / bufferLength

    context.lineWidth = 2
    context.strokeStyle = '#000000'
    context.clearRect(0, 0, width, height)

    context.beginPath()
    context.moveTo(0, height / 2)
    for (let i = 0; i < bufferLength; i++) {
      const y = (dataArray[i] / 255.0) * height
      context.lineTo(x, y)
      x += sliceWidth
    }
    context.lineTo(width, height / 2)
    context.stroke()
  }, [])

  const tick = useCallback(() => {
    draw()
    rafId.current = requestAnimationFrame(tick)
  }, [draw])

  useEffect(() => {
    let localSource: MediaStreamAudioSourceNode | MediaElementAudioSourceNode | undefined

    if (audioSrc instanceof MediaStream) {
      localSource = audioContext.current.createMediaStreamSource(audioSrc)
    } else if (audioSrc instanceof HTMLAudioElement) {
      audioRef.current = audioSrc
      localSource = audioContext.current.createMediaElementSource(audioSrc)
    }

    // Capture the current state of the refs to use in the cleanup
    const localAnalyser = analyser.current
    const localGainNode = gainNode.current

    localSource?.connect(localAnalyser)
    localAnalyser.connect(localGainNode)
    localGainNode.connect(audioContext.current.destination)

    rafId.current = requestAnimationFrame(tick)

    return () => {
      if (rafId.current === undefined) {
        return
      }
      if (rafId.current !== null) {
        cancelAnimationFrame(rafId.current)
      }
      // Use the locally captured states
      localAnalyser.disconnect()
      localGainNode.disconnect()
      localSource?.disconnect()
    }
  }, [audioSrc, tick])

  return (
    <Box
      component="canvas"
      ref={canvasRef}
      sx={[
        {
          width: '100%',
          height: '100%',
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    />
  )
}

export default AudioVisualizer
